import {
  TrialPopupType,
  SignInUpMethod,
  UseCase,
  FileInteractionLocation,
  FileDownloadStatus,
  CustomerCategory,
  LogoStatus,
} from './literal-types';

export enum PlatformName {
  GTM = 'GTM',
}

export type EventsPayload = {
  user_data: {
    [PlatformName.GTM]: {
      user_id: number;
      user_usecase: UseCase;
      customer_category: CustomerCategory;
    };
  };
  sign_up: {
    [PlatformName.GTM]: {
      method: SignInUpMethod;
      accounts_on_device: number;
      send_marketing_email?: 'allow' | 'deny';
    };
  };
  sign_up_v2: {
    [PlatformName.GTM]: {
      method: SignInUpMethod;
      accounts_on_device: number;
      send_marketing_email?: 'allow' | 'deny';
    };
  };
  onboarding_submit: {
    [PlatformName.GTM]: {
      usecase: UseCase;
    };
  };
  login: {
    [PlatformName.GTM]: {
      method: SignInUpMethod;
    };
  };
  trial_popup_close: {
    [PlatformName.GTM]: {
      popup_name: TrialPopupType;
    };
  };
  trial_popup_to_checkout: {
    [PlatformName.GTM]: {
      popup_name: TrialPopupType;
    };
  };
  payment_refuse_survey_close: {
    [PlatformName.GTM]: Record<string, never>;
  };
  payment_refuse_survey_to_checkout: {
    [PlatformName.GTM]: Record<string, never>;
  };
  payment_refuse_survey_answer: {
    [PlatformName.GTM]: {
      payment_refuse_reason: string;
    };
  };
  click_generation_start: {
    [PlatformName.GTM]: {
      style_name: string;
      flow: 'workspace' | null;
      font: string;
      logo: LogoStatus;
      processed_file_id: string;
    };
  };
  style_change_save: {
    [PlatformName.GTM]: {
      flow: 'workspace' | null;
      style_name: string;
      font: string;
      logo: LogoStatus;
      processed_file_id?: string;
    };
  };
  service_error_system: {
    [PlatformName.GTM]: {
      action: string;
      fileID?: string;
      httpCode?: number;
      message: string;
    };
  };
  service_error_preview: {
    [PlatformName.GTM]: {
      message: string;
      action: string;
      class: string;
      inner_message: string;
      trace: string;
    };
  };
  file_generation_start: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace' | null;
    };
  };
  file_generation_first_slide: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace' | null;
    };
  };
  file_generation_done: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace' | null;
    };
  };
  scratch_generation_first_slide_start: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      interaction_location: 'advanced_mode' | 'raw_mode';
      flow: 'workspace' | null;
    };
  };
  scratch_generation_first_slide_done: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace' | null;
    };
  };
  generation_slide_start: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      interaction_location: 'advanced_mode' | 'raw_mode';
      flow: 'workspace' | null;
    };
  };
  generation_slide_done: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace' | null;
    };
  };
  generation_add_slide: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace' | null;
    };
  };
  generation_delete_slide: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace' | 'text-to-slides' | null;
    };
  };
  generation_duplicate_slide: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace' | null;
    };
  };
  generation_move_slide: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace' | null;
    };
  };
  design_editing: {
    [PlatformName.GTM]: {
      editing_location: 'preview_interaction' | 'menu_interaction';
      editable_element: string;
      processed_file_id: string;
    };
  };
  design_editing_canvas: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'workspace';
    };
  };
  click_file_download: {
    [PlatformName.GTM]: {
      file_interaction_location: FileInteractionLocation;
      file_type: 'pptx' | 'pdf';
      file_slides_count: number;
      style: string;
      processed_file_id: string;
      flow: 'workspace' | null;
    };
  };
  file_download: {
    [PlatformName.GTM]: {
      file_download_status: FileDownloadStatus;
      redirect_place?: 'confirm_email';
      file_interaction_location: FileInteractionLocation;
      flow: 'workspace' | null;
      file_type: 'pptx' | 'pdf';
      processed_file_id: string;
    };
  };
  file_create_duplicate: {
    [PlatformName.GTM]: Record<string, never>;
  };
  file_rename: {
    [PlatformName.GTM]: {
      file_interaction_location: FileInteractionLocation;
    };
  };
  file_delete: {
    [PlatformName.GTM]: Record<string, never>;
  };
  slideshow_started: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      file_interaction_location: 'shared_page' | 'workspace';
    };
  };
  share: {
    [PlatformName.GTM]: {
      method: 'link_access' | 'pin_access';
      file_url: string;
      file_interaction_location: FileInteractionLocation;
      flow: 'workspace' | null;
    };
  };
  begin_checkout: {
    [PlatformName.GTM]: {
      ecommerce: {
        currency: string;
        value: number;
        payment_method: string;
        product_id: number;
        items: Array<{
          item_name: 'subscription' | 'pay_as_you_go';
          item_variant: string;
          price: number;
          quantity: number;
        }>;
      };
    };
  };
};

export type EventTriggerNames = keyof EventsPayload;
